import React, { useEffect, useState } from 'react';
import { Icons } from '../../constants';

export interface ToastProps {
    id: string;
    title?: string;
    description?: string;
    message?: string; // Fallback
    type?: 'success' | 'error' | 'info' | 'default' | 'undo' | 'warning';
    actionLabel?: string;
    onAction?: () => void;
    duration?: number;
    onClose: () => void;
}

export const Toast = ({
    title,
    description,
    message,
    type = 'default',
    actionLabel,
    onAction,
    duration = 5000,
    onClose
}: ToastProps) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        requestAnimationFrame(() => setIsVisible(true));
        const timer = setTimeout(() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
        }, duration);
        return () => clearTimeout(timer);
    }, [duration, onClose]);

    const handleDismiss = () => {
        setIsVisible(false);
        setTimeout(onClose, 300);
    };

    // Theme Config
    const themes = {
        success: {
            bg: 'bg-sky-50 dark:bg-sky-950/30',
            border: 'border-sky-100 dark:border-sky-800/30',
            iconBg: 'bg-sky-100 dark:bg-sky-900/50',
            iconColor: 'text-sky-600 dark:text-sky-400',
            icon: Icons.Check || Icons.Activity // Fallback if Check missing
        },
        error: {
            bg: 'bg-red-50 dark:bg-red-950/30',
            border: 'border-red-100 dark:border-red-800/30',
            iconBg: 'bg-red-100 dark:bg-red-900/50',
            iconColor: 'text-red-600 dark:text-red-400',
            icon: Icons.AlertCircle || Icons.AlertTriangle
        },
        warning: {
            bg: 'bg-amber-50 dark:bg-amber-950/30',
            border: 'border-amber-100 dark:border-amber-800/30',
            iconBg: 'bg-amber-100 dark:bg-amber-900/50',
            iconColor: 'text-amber-600 dark:text-amber-400',
            icon: Icons.AlertTriangle
        },
        undo: {
            bg: 'bg-zinc-900 dark:bg-white',
            border: 'border-zinc-800 dark:border-zinc-200',
            iconBg: 'bg-zinc-800 dark:bg-zinc-100',
            iconColor: 'text-white dark:text-black',
            textColor: 'text-white dark:text-black',
            icon: Icons.Undo
        },
        default: {
            bg: 'bg-white dark:bg-zinc-900',
            border: 'border-slate-200 dark:border-zinc-800',
            iconBg: 'bg-slate-100 dark:bg-zinc-800',
            iconColor: 'text-slate-600 dark:text-slate-400',
            icon: Icons.Info
        },
        info: { // same as default
            bg: 'bg-white dark:bg-zinc-900',
            border: 'border-slate-200 dark:border-zinc-800',
            iconBg: 'bg-slate-100 dark:bg-zinc-800',
            iconColor: 'text-slate-600 dark:text-slate-400',
            icon: Icons.Info
        }
    };

    const currentTheme = themes[type as keyof typeof themes] || themes.default;
    const Icon = currentTheme.icon;

    // Display Logic
    const displayTitle = title || message;
    const displayDesc = description;

    return (
        <div
            className={`
                transform transition-all duration-300 ease-spring
                ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'}
            `}
        >
            <div className={`
                flex items-center gap-4 pl-2 pr-6 py-2 rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.04)]
                border backdrop-blur-sm
                ${currentTheme.bg} ${currentTheme.border}
            `}>
                {/* Icon Circle */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${currentTheme.iconBg}`}>
                    {Icon && <Icon className={`w-5 h-5 ${currentTheme.iconColor}`} />}
                </div>

                {/* Text Content */}
                <div className="flex flex-col min-w-[160px]">
                    <h4 className={`text-sm font-semibold ${type === 'undo' ? 'text-white dark:text-black' : 'text-slate-800 dark:text-slate-100'}`}>
                        {displayTitle}
                    </h4>
                    {displayDesc && (
                        <p className={`text-xs ${type === 'undo' ? 'text-zinc-400 dark:text-zinc-500' : 'text-slate-500 dark:text-slate-400'}`}>
                            {displayDesc}
                        </p>
                    )}
                </div>

                {/* Action Button (e.g. Undo) */}
                {actionLabel && onAction && (
                    <button
                        onClick={onAction}
                        className="ml-2 px-3 py-1.5 text-xs font-bold rounded-full bg-white/10 hover:bg-white/20 text-indigo-500 transition-colors"
                    >
                        {actionLabel}
                    </button>
                )}

                {/* Close Button */}
                <button
                    onClick={handleDismiss}
                    className="ml-2 p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                >
                    <Icons.X className="w-3.5 h-3.5 opacity-40 hover:opacity-100" />
                </button>
            </div>
        </div>
    );
};
